import { Component, OnDestroy, ViewChild, TemplateRef, AfterViewInit, Input, Output, EventEmitter } from '@angular/core';

import { Subscription, forkJoin, Observable } from 'rxjs';
import { tap, map, switchMap, catchError, filter, take } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { AbstractDialogComponent } from '../../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { TableColumnModel } from '../../../../../Common/Forms/models/table-column.model';
import { TableColumnInputEnum } from '../../../../../Common/Forms/enums/table-column-input-type.enum';
import { TargetingListService } from '../../../services/targeting-list.service';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { TargetingListList } from '../../../models/targeting-list-list.model';
import { TargetingResultModel } from '../../../models/targeting-result.model';
import { ChipModel } from '../../../../../Common/Forms/models/chip.model';
import { TextInputDialogModel } from '../../../../../Common/Dialogs/models/text-input-dialog.model';
import { TextInputDialogComponent } from '../../../../../Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { UsersService } from '../../../../../Core/Users/services/users.service';
import { DateHelper } from '../../../../../Common/Forms/helpers/date.helper';
import { TargetingListsApiService } from '../../../../../api/targeting/targeting-lists.api.service';
import { EntityType } from '../../../../../api/targeting/enum/entity.type.enum';
import { DataRow } from '../../../../../Common/RhpContexts/components/ListPickerDialog/list-picker-dialog.component';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { OutletModel } from '../../../models/outlet.model';
import { ContactModel } from '../../../../../Common/TargetingShared/model/contact.model';
import { EntityIdApiModel } from '../../../../../api/targeting/model/entity-id-api-model';
import { AutocompleteCustomOption, AppearCondition } from '../../../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { EntityTypeDefaultImageEnum } from '../../../../../Common/Misc/enums/entity-type-default-image.enum';
import { TargetingList } from '../../../models/targeting-list.model';
import { CommonApiErrorEnum } from '../../../../../api/genericApi/enums/common-api-error.enum';

@Component({
    selector: 'agpr-manage-list-dialog',
    templateUrl: './manage-list-dialog.component.html',
    styleUrls: ['./manage-list-dialog.component.scss'],
    standalone: false
})
export class ManageListDialogComponent extends AbstractDialogComponent implements OnDestroy, AfterViewInit {

    @Input() set setEntity(entity: TargetingResultModel | ContactModel | OutletModel) {
        this.focusedEntity = entity;
        this.entityListKey = this.focusedEntity?.entityType === EntityType.CONTACT ? 'contactUuid' : 'outletUuid';

        this.subscriptions.push(
            this.targetingListsApiService.getListMemberships([{
                [this.entityListKey]: this.focusedEntity?.id
            }]).subscribe(listMemberships => {
                this.listMembershipIds = listMemberships[0]?.targetingLists?.map(list => list.id) || [];
                this.populateListMembershipInitialSelection();
            })
        );
    }

    @Output() listMembershipChanged: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('countInfoCell') public countCellTemplate: TemplateRef<any>;

    readonly buttonTypes = ButtonTypesEnum;
    readonly entityTypes = EntityType;
    readonly messageSeverityEnum = messageSeverityEnum;

    subscriptions: Subscription[] = [];

    id = 'manageList';
    loading: boolean = false;
    listMembershipIds: number[];
    focusedEntity: TargetingResultModel | ContactModel | OutletModel;
    imageDefault = EntityTypeDefaultImageEnum;

    dataRows: DataRow[];
    filteredRows: DataRow[] = [];
    freshCreatedId: number;

    initialChips: ChipModel[] = [];
    listSummaryChips: ChipModel[] = [];
    addedList: string[] = [];
    removedList: string[] = [];
    entityListKey: string;

    nameColumn: TableColumnModel = {
        type: TableColumnInputEnum.text,
        heading: 'Discover.listBuilder.table.listname',
        field: 'name',
        tooltipField: 'name',
        sortable: true,
        sortType: 'text',
        selectable: true,
        selectField: 'selected',
        width: 250
    };

    countColumn: TableColumnModel = {
        type: TableColumnInputEnum.custom,
        heading: 'Discover.listBuilder.table.contacts',
        field: 'count',
        width: 150,
        sortable: true
    };

    columnDefs: TableColumnModel[] = [];

    listsAutocompleteOptions: AutocompleteCustomOption[] = [{
        label: 'Discover.manageList.createList',
        action: (listName: string) => {
            if (listName.length === 0) {

                return;
            }

            this.loading = true;

            this.targetingListService.isNameDuplicated$(listName).pipe(
                switchMap((duplicate) => {
                    if (duplicate) {
                        throw new Error(CommonApiErrorEnum.duplicateName);
                    } else {
                        const list = new TargetingList({name: listName});
                        return this.targetingListService.create(list);
                    }
                })
            ).subscribe({
                next: (id) => {
                    this.freshCreatedId = id;
                    this.loading = false;
                    this.newlyAddedList();
                },
                error: () => {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.dialogs.newList.duplicate',
                    });
                    this.resetLists();
                    this.loading = false;
                }
            });
        },
        appearCondition: this.filteredRows.length > 0 ? null : AppearCondition.always,
        clearOnSelect: false
    }];

    constructor(
        public coreDialogService: CoreDialogService,
        public targetingListService: TargetingListService,
        public usersService: UsersService,
        private messageService: MessageService,
        private targetingListsApiService: TargetingListsApiService
    ) {
        super(coreDialogService, 'ManageList');

        this.subscriptions.push(
            this.targetingListService.all$.subscribe(lists => {
                if (!lists) {
                    return;
                }
                this.dataRows = lists.map(list => {
                    return <DataRow>{
                        id: list.id,
                        selected: !!this.listSummaryChips.find( x => x.label === list.name),
                        name: list.name,
                        count: list.count,
                        originalData: list,
                        createdDate: DateHelper.toDateString(list.createdDate),
                        modifiedDate: DateHelper.toDateString(list.modifiedDate),
                    };
                });
                this.filteredRows = this.dataRows;

                this.fetchUserNames();
                this.newlyAddedList();
            })
        );
    }

    ngAfterViewInit() {
        requestAnimationFrame(() => {
            this.countColumn.customTemplate = this.countCellTemplate;
            this.columnDefs = [
                this.nameColumn,
                this.countColumn
            ];
        });
    }

    newlyAddedList() {
        if (this.freshCreatedId) {
            const freshCreatedRow = this.dataRows.find(x => x.id === this.freshCreatedId);
            if (freshCreatedRow) {
                freshCreatedRow.selected = true;
                this.listSummaryChips.push(<ChipModel>{
                    label : freshCreatedRow.name
                });
            }
            this.updateChangeReceipt();
            this.freshCreatedId = undefined;
        }
    }

    removeChip(index: number) {
        const chipName = this.listSummaryChips[index]?.label;
        this.listSummaryChips.splice(index, 1);
        this.dataRows.find(x => x.name === chipName).selected = false;
        this.updateChangeReceipt();
    }

    private populateListMembershipInitialSelection() {
        if (this.dataRows && this.listMembershipIds.length > 0) {
            this.listMembershipIds.forEach(id => {
                const list = this.dataRows.find(x => x.id === id);
                if (list) {
                    list.selected = true;
                    this.initialChips.push(<ChipModel>{label: list.name});
                    this.listSummaryChips.push(<ChipModel>{label: list.name});
                }
            });
        }
    }

    /**
     * Compare initial list membership state with current state, updating addedList & removedList with changes
     */
    private updateChangeReceipt() {
        this.listSummaryChips.forEach(list => {
            if (!this.initialChips.find(x => x.label === list.label) && !this.addedList.includes(list.label)) {
                this.addedList.push(list.label);
            }
        });

        this.initialChips.forEach(list => {
            if (!this.listSummaryChips.find(x => x.label === list.label) && !this.removedList.includes(list.label)) {
                this.removedList.push(list.label);
            }
        });

        this.addedList.forEach((list, index) => {
            if (!this.listSummaryChips.find(x => x.label === list)) {
                this.addedList.splice(index, 1);
            }
        });

        this.removedList.forEach((list, index) => {
            if (this.listSummaryChips.find(x => x.label === list)) {
                this.removedList.splice(index, 1);
            }
        });

        // update array reference for JoinList pipe
        this.addedList = this.addedList.slice();
        this.removedList = this.removedList.slice();
    }

    updateLists() {
        this.loading = true;
        const apiCalls: Observable<void>[] = [];

        const listsToAdd: TargetingListList[] = this.dataRows
            .filter(x => this.addedList.includes(x.name))
            .map(x => x.originalData);

        const listsToRemove: TargetingListList[] = this.dataRows
            .filter(x => this.removedList.includes(x.name))
            .map(x => x.originalData);

        if (listsToAdd.length > 0) {
            const entityApiModel: EntityIdApiModel = {
                [this.entityListKey]: this.focusedEntity.id
            };

            if (
                (this.focusedEntity as ContactModel).selectedEmployment &&
                (this.focusedEntity as ContactModel).selectedEmployment.id
            ) {
                entityApiModel.employmentId = (this.focusedEntity as ContactModel).selectedEmployment.id;
            }

            apiCalls.push(this.targetingListsApiService.addEntitiesToList(
                listsToAdd.map(x => x.id),
                [entityApiModel]
            ));
        }

        listsToRemove.forEach(list => {
            apiCalls.push(
                this.targetingListsApiService.removeEntitiesFromList(
                    list.id, [<EntityIdApiModel>{[this.entityListKey]: this.focusedEntity.id}]
                )
            );
        });


        forkJoin(apiCalls).pipe(
            catchError((error) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Discover.manageList.failed.heading',
                    detail: 'Discover.manageList.failed.message',
                });

                this.closeDialog();

                throw new Error(error);
            }),
            switchMap(() => {
                return this.targetingListService.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => null)
                );
            }),
        ).subscribe({
            next: () => {
                this.loading = false;

                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Discover.manageList.success.heading',
                    detail: 'Discover.manageList.success.message',
                });
                this.targetingListService.listMembeshipsChanged(this.focusedEntity.id);
                this.listMembershipChanged.emit();
                this.closeDialog();
            }
        });
    }

    selectionChanged(item: DataRow) {
        if (item.selected) {
            this.listSummaryChips.push(<ChipModel>{ label: item.name });
        } else {
            this.listSummaryChips.splice(this.listSummaryChips.findIndex(x => x.label === item.name), 1);
        }
        this.listSummaryChips = this.listSummaryChips.slice();
        this.updateChangeReceipt();
    }

    selectAll(state: boolean) {
        if (state) {
            this.listSummaryChips = this.filteredRows.map(x => <ChipModel>{ label: x.name });
            this.addedList = this.filteredRows.map(x => x.name);
            this.removedList = [];
        } else {
            this.listSummaryChips = [];
            this.addedList = [];
            this.removedList = this.initialChips.map(x => x.label);
        }
    }

    resetLists() {
        this.filteredRows = this.dataRows;
    }

    filterRows(searchString: string) {
        if (searchString.length === 0) {
            this.filteredRows = this.dataRows;
        }
        this.filteredRows = this.dataRows.filter(row => row.name.toLowerCase().indexOf(searchString.toLowerCase()) > -1);
        this.listsAutocompleteOptions.find(x => x.label === 'Discover.manageList.createList').appearCondition
            = this.filteredRows.length > 0 ? null : AppearCondition.always;
    }

    createNewList() {
        const io: TextInputDialogModel = {
            inputs: {
                heading: 'Discover.dialogs.newList.heading',
                description: 'Discover.dialogs.newList.description',
                placeholder: 'Discover.dialogs.newList.placeholder',
                okLabel: 'Discover.dialogs.newList.createList',
                cancelLabel: 'Discover.dialogs.universal.cancelLabel',
                width: 300,
                disabledButtonTooltip: 'Discover.dialogs.newList.listNameRequired',
                submit: (listName: string) => {
                    const newList = new TargetingList({name: listName});
                    return this.targetingListService.create(newList).pipe(
                        tap(id => {
                            this.freshCreatedId = id;
                            this.newlyAddedList();
                        })
                    );
                },
                observableFormValidator: (listName: string) => {
                    return this.targetingListService.isNameDuplicated$(listName).pipe(
                        map((isDuplicate) => {
                            if (isDuplicate) {
                                return ['Discover.dialogs.newList.duplicate'];
                            } else {
                                return [];
                            }
                        })
                    );
                }
            },
            outputs: {}
        };
        this.coreDialogService.open(TextInputDialogComponent, io);
    }


    /**
     * Converts user ids to usernames for the info tooltip
     */
    private fetchUserNames() {
        const userIds = [...this.dataRows.map(x => +x.originalData.createdByUserId), ...this.dataRows.map(x => +x.originalData.modifiedByUserId)];
        this.subscriptions.push(
            this.usersService.getUsers(userIds).subscribe(users => {
                this.dataRows.forEach(row => {
                    const creator = users.find(x => x.id === row.originalData.createdByUserId);
                    if (creator) {
                        row.createdUser = creator.getDisplayName();
                    }

                    const modifier = users.find(x => x.id === row.originalData.modifiedByUserId);
                    if (modifier) {
                        row.modifiedUser = modifier.getDisplayName();
                    }
                });
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
